import { Controller } from "@hotwired/stimulus"

/**
 * AI Vector Map Controller — CONEK-4998.
 *
 * Renders a 3D scatter of embedding points (reduced server-side via PCA) using
 * Plotly. Points are grouped/colored by `group` (entity_type), with the label
 * shown on hover.
 *
 * Plotly is loaded via dynamic `import()` inside connect() so it lands in its
 * own Vite chunk — the admin shell stays small until this page actually mounts
 * (mirrors the shiki lazy-load in ai/json_inspector_controller.js).
 *
 * Points arrive as a JSON array on the `points` value:
 *   [{ id, label, group, x, y, z }, ...]
 */
export default class extends Controller {
  static values = { points: Array, loadErrorMessage: String }
  static targets = ["plot"]

  async connect() {
    if (this.pointsValue.length === 0) return

    // gl3d-only bundle (~1.7MB vs ~4.8MB full dist) — includes scatter3d and
    // nothing else. The full plotly.js-dist-min blew the Vite build heap (OOM)
    // because it isn't isolated into its own chunk; gl3d + the manualChunks
    // split in vite.config.ts keep peak build memory under the CI limit.
    let Plotly
    try {
      Plotly = (await import("plotly.js-gl3d-dist-min")).default
    } catch (error) {
      // A chunk-load failure (network blip, asset-host hiccup, blocker) would
      // otherwise leave a blank div + unhandled rejection. Show a fallback.
      console.error("[vector-map] failed to load Plotly chunk", error)
      if (this.hasPlotTarget) {
        this.plotTarget.textContent = this.loadErrorMessageValue
      }
      return
    }
    this.plotly = Plotly
    Plotly.newPlot(this.plotTarget, this.traces(), this.layout(), {
      responsive: true,
      displaylogo: false,
    })
  }

  // Plotly hover text interprets a small set of pseudo-HTML tags, so any
  // user-controlled value (label/detail come from editable entity name/title
  // fields) must be HTML-escaped before it is concatenated into the markup
  // string — otherwise a tenant could plant markup that renders in an admin's
  // browser when they open the vector map. CONEK-4998 (review).
  escapeHtml(value) {
    return String(value ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;")
  }

  disconnect() {
    if (this.plotly && this.hasPlotTarget) {
      this.plotly.purge(this.plotTarget)
    }
  }

  // One Plotly trace per group so the legend lets you toggle entity_types and
  // each gets a distinct color automatically.
  traces() {
    const groups = new Map()
    for (const p of this.pointsValue) {
      const key = p.group || "unknown"
      if (!groups.has(key)) groups.set(key, { x: [], y: [], z: [], text: [] })
      const g = groups.get(key)
      g.x.push(p.x)
      g.y.push(p.y)
      g.z.push(p.z)
      g.text.push(this.hoverText(p))
    }

    return [...groups.entries()].map(([name, g]) => ({
      type: "scatter3d",
      mode: "markers",
      name,
      x: g.x,
      y: g.y,
      z: g.z,
      text: g.text,
      hovertemplate: "%{text}<extra>" + name + "</extra>",
      marker: { size: 3, opacity: 0.8 },
    }))
  }

  // Multi-line hover: primary label, the human name/title from metadata, and
  // the created date — far more useful than a bare id.
  hoverText(p) {
    const lines = ["<b>" + this.escapeHtml(p.label) + "</b>"]
    if (p.detail) lines.push(this.escapeHtml(p.detail))
    if (p.created_at)
      lines.push(
        '<span style="color:#999">' + this.escapeHtml(p.created_at) + "</span>",
      )
    return lines.join("<br>")
  }

  layout() {
    return {
      margin: { l: 0, r: 0, t: 0, b: 0 },
      paper_bgcolor: "rgba(0,0,0,0)",
      scene: {
        xaxis: { title: "PC1" },
        yaxis: { title: "PC2" },
        zaxis: { title: "PC3" },
      },
      legend: { itemsizing: "constant" },
    }
  }
}
